import { TIER_NAMES } from "../game/ladder";
import type { Goal } from "../game/run";
import { buttonFor, type InputKind } from "./input-names";
import { LetterTiles, letterTitle } from "./LetterTiles";

/** One goal's row: checked when done, its progress in a run, and in free skate its lock and stat point badge (#66). */
function GoalRow({ goal: g, run, lockLine = true }: { goal: Goal; run: boolean; lockLine?: boolean }) {
  const locked = !run && !g.done && Boolean(g.needs);
  return (
    <li
      className={`flex items-baseline justify-between gap-2 rounded-md border-2 border-ink px-3 py-1 text-sm font-black uppercase italic ${g.done ? "bg-aqua-soft text-ink" : "bg-surface-soft text-on-surface opacity-80"}`}
    >
      <span className="min-w-0">
        <span aria-hidden="true" className="mr-2 inline-block w-3">
          {g.done ? "✓" : locked ? "🔒" : "·"}
        </span>
        {run && g.progress != null ? letterTitle(g.id, g.text) : g.text}
        <span className="sr-only">{g.done ? " (done)" : locked ? " (locked)" : " (not done)"}</span>
        {locked && lockLine && <span className="block text-xs font-semibold normal-case not-italic">Unlocks after: {g.needs}</span>}
      </span>
      {run && g.progress != null && !g.done && (
        <span className="shrink-0">
          <LetterTiles id={g.id} progress={g.progress} />
        </span>
      )}
      {!run && Boolean(g.statPoints) && (
        <span
          className="shrink-0 rounded-md border-2 border-ink bg-aqua-soft px-1.5 text-xs not-italic tabular-nums text-ink"
          aria-label={`pays ${g.statPoints} stat point${g.statPoints === 1 ? "" : "s"}`}
        >
          +{g.statPoints}
        </span>
      )}
    </li>
  );
}

/**
 * The run's goals, or outside a run the map's challenges, checked off as
 * they're done (a goal done in a run or a challenge counts for both). Outside
 * a run the challenges are a ladder (#66): grouped by tier, with locks and
 * the stat points each one pays. On a map with hubs (#86) each tier is its
 * hub's list, locked as a whole, so its lock line shows once under its name (#87).
 */
export function GoalList({ goals, run, hubs = false, input = "keyboard" }: { goals: readonly Goal[]; run: boolean; hubs?: boolean; input?: InputKind }) {
  const grind = buttonFor(input, "△");
  const done = goals.filter((g) => g.done).length;
  const tiers = run ? [] : [...new Set(goals.map((g) => g.tier).filter((t) => t !== undefined))].sort();
  return (
    <section className="mb-3">
      <p className="text-center text-sm font-black uppercase italic tracking-widest">
        {run ? `${done} of ${goals.length} goals` : `${done} of ${goals.length} done`}
      </p>
      {!run && (
        <p className="text-center text-xs font-semibold opacity-75">
          {hubs
            ? `Each tier has a hub in the park: skate into it and press ${grind} for its list, or chase them all in a 2-minute run. Finish a tier to open the next hub; green badges pay stat points.`
            : `Skate into a glowing marker and press ${grind} to take one on, or chase them all in a 2-minute run. Finish easier ones to unlock the rest; green badges pay stat points.`}
        </p>
      )}
      {tiers.length > 0 ? (
        tiers.map((t) => {
          const rows = goals.filter((g) => g.tier === t);
          // A hub's tier is locked as a whole: every goal on it waits on the same thing.
          const lock = hubs ? (rows.find((g) => !g.done && g.needs)?.needs ?? null) : null;
          return (
            <div key={t} className="mt-2">
              <h3 id={`tier-${t}`} className="text-xs font-black uppercase tracking-widest">
                {hubs ? `Tier ${t} hub` : `Tier ${t}`} · {TIER_NAMES[t - 1]}
                {hubs && rows.length > 0 && rows.every((g) => g.done) && " ✓"}
              </h3>
              {lock && <p className="text-xs font-semibold">🔒 Unlocks when you {lock.replace(/^F/, "f")}</p>}
              <ul aria-labelledby={`tier-${t}`} className="mt-1 flex flex-col gap-1">
                {rows.map((g) => (
                  <GoalRow key={g.id} goal={g} run={run} lockLine={!hubs} />
                ))}
              </ul>
            </div>
          );
        })
      ) : (
        <ul aria-label="Goals" className="mt-2 flex flex-col gap-1">
          {goals.map((g) => (
            <GoalRow key={g.id} goal={g} run={run} />
          ))}
        </ul>
      )}
    </section>
  );
}
